import { NgRedux, select } from '@ptl/angular-redux-store';
import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelectChange } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { BaseAgency, EntitlementsHelperService, IndividualMarketAgency, SeniorAgency } from '@ptl/account-management';
import { StateStructure } from '@ptl/state';
import { Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AuthoringModeService } from 'src/app/shared/services/authoring-mode.service';
import { DateService } from 'src/app/shared/services/date.service';
import { ResourceFilter } from './configs/reducers/interfaces/interfaces/resource-filters.interface';
import { RESOURCES_LIST_REDUCER, toggleFlagResourceCategory, toggleDisabledResourceCategory, resetResourceCategories, createTag, toggleFlagResourceCategories } from './configs/reducers/resources-list.reducer';
import { ResourcesListEmailComponent } from './email-modal/resources-list-email/resources-list-email.component';
import { ResourcesListItem } from './services/interfaces/ResourcesListDAM.response.interface';
import { ResourcesListService } from './services/resources-list.service';
import { GlobalShadowStyleDirective } from '@ptl/global-components';

interface ResourcesListColumnFilterControls {
  type: UntypedFormControl;
  topic: UntypedFormControl;
  dateRange: UntypedFormControl;
  fileSearch: UntypedFormControl;
}
interface ResourcesListFilterOptions {
  type: { options: any[]; };
  topic: { options: any[]; };
  dateRange: { options: any[]; };
  fileSearch: string;
}

@Component({
  selector: 'app-resources-list',
  templateUrl: './resources-list.component.html',
  styleUrls: ['./resources-list.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false
})

export class ResourcesListComponent extends GlobalShadowStyleDirective implements OnInit, AfterViewInit, OnDestroy {

  /** AEM Config */
  /**
   * 'shopx:resources-list-tags/market/individual,shopx:resources-list-tags/market/middle-market-51-99'
   */
  @Input() tags: string;

  @Input() servicePath: string;
  @Input() docRoot: string;
  @Input() alwaysCheckedMarketTags: string;

  /**
   * Comma-separated list of type filter options
   * Example: 'PDF,Video,Spreadsheet,Form,Presentation'
   */
  @Input() typeTags: string;

  /**
   * Comma-separated list of topic filter options
   * Example: 'Enrollment,Benefits,Claims,Provider Network'
   */
  @Input() topicTags: string;

  /** AEM Content */
  @Input() resourceListHeader: string;
  @Input() resourceCheckboxRefineHeader: string;
  @Input() typeRefineHeader: string;
  @Input() topicRefineHeader: string;
  @Input() uncheckAllContent: string;
  @Input() checkAllContent: string;

  @Input() checkAgencyRegion: string;
  @Input() showDescriptions: string;



  /** Markets/Categories changed */
  @select(['componentStates', RESOURCES_LIST_REDUCER, 'filters', 'resourceCategories'])
  resourceCategories$: Observable<ResourceFilter[]>;

  /** Types changed */
  @select(['componentStates', RESOURCES_LIST_REDUCER, 'filters', 'resourceTypes'])
  resourceTypes$: Observable<ResourceFilter[]>;

  /** Topics changed */
  @select(['componentStates', RESOURCES_LIST_REDUCER, 'filters', 'resourceTopics'])
  resourceTopics$: Observable<ResourceFilter[]>;

  typeControlChanges: Subscription;
  topicControlChanges: Subscription;

  /** When state is updated, check if all valid checkboxes are enabled */
  allValidCheckboxesChecked: boolean;
  selectedAgency: IndividualMarketAgency | SeniorAgency | BaseAgency;

  /** Data Source + Configs */
  dataSet: ResourcesListItem[];
  resourcesDataSource: MatTableDataSource<ResourcesListItem>;

  /** Pagination tracking */
  totalCount: number = 0;
  currentPageSize: number = 25;

  /** Static filter options from @Input */
  configuredTypeOptions: string[] = [];
  configuredTopicOptions: string[] = [];

  /** Filter counts from server */
  typeCounts = new Map<string, number>();
  topicCounts = new Map<string, number>();

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild("ResourcesListPaginator") paginator: MatPaginator;

  displayedColumns = [
    'fileName',
    'type',
    'lastModifiedDate',
    'topic',
    'email',
    'download'
  ];

  /** FORMLESS control collection */
  tableFilterControls: ResourcesListColumnFilterControls = {
    type: new UntypedFormControl(),
    topic: new UntypedFormControl(),
    fileSearch: new UntypedFormControl(),
    dateRange: new UntypedFormControl()
  };

  /** OPTIONS for the DROPDOWN controls */
  tableFilterSelectObj: ResourcesListFilterOptions = {
    type: {
      options: []
    },
    topic: {
      options: []
    },
    dateRange: {
      options: [
        { label: 'All Time', daysMinus: 2000 },
        { label: 'Past Week', daysMinus: 7 },
        { label: 'Past 2 Weeks', daysMinus: 14 },
        { label: 'Past Month', daysMinus: 30 },
        { label: 'Past 6 Months', daysMinus: 180 }
      ]
    },
    fileSearch: ''
  };

  sortMap: Map<string, 'asc' | 'desc'>;

  isAuthoring = false;


  constructor(
    private ngRedux: NgRedux<StateStructure>,
    private resourcesListService: ResourcesListService,
    private matDialog: MatDialog,
    private entitlementsHelperService: EntitlementsHelperService,
    private authoringMode: AuthoringModeService,
    private date: DateService
  ) {
    super();
   }

  get parsedMarketTags() {
    if (this.tags && this.tags.length > 0) {
      try {
        const tagsArray = this.tags.split(',');
        return tagsArray;
      } catch (e) {
        return [];
      }
    } else {
      return [];
    }
  }

  get parsedAlwaysCheckedMarketTags() {
    if (this.tags && this.tags.length > 0) {
      try {
        const tagsArray = this.tags.split(',');
        return tagsArray;
      } catch (e) {
        return [];
      }
    } else {
      return [];
    }
  }

  get parsedTypeTags() {
    if (this.typeTags && this.typeTags.length > 0) {
      try {
        const tagsArray = this.typeTags.split(',');
        return tagsArray;
      } catch (e) {
        return [];
      }
    } else {
      return [];
    }
  }

  get parsedTopicTags() {
    if (this.topicTags && this.topicTags.length > 0) {
      try {
        const tagsArray = this.topicTags.split(',');
        return tagsArray;
      } catch (e) {
        return [];
      }
    } else {
      return [];
    }
  }

  get showDescriptionConfig() {
    return this.showDescriptions === 'true' ? true : false;
  }

  parseDate(dateString?: string): Date | null {
    if (!dateString) return null;

    const parts = dateString.split('/');
    if (parts.length !== 3) return null;

    const [month, day, year] = parts.map(Number);
    return new Date(year, month - 1, day);
  }

  async ngOnInit() {
    this.isAuthoring = this.authoringMode.getAuthoringMode();

    this.ngRedux.dispatch(resetResourceCategories());

    /** CONFIGURE market tags: show/check/disable */
    this.parsedMarketTags.forEach(tag => {

      this.ngRedux.dispatch(createTag(tag));

      let alwaysCheckedMarketTags = this.alwaysCheckedMarketTags || [];
      if (alwaysCheckedMarketTags.indexOf(tag) !== -1) {
        this.ngRedux.dispatch(toggleDisabledResourceCategory(tag));
      }

    });

    /** Setup static dropdown options from @Input */
    this.tableFilterSelectObj.type.options = this.parseFilterTags(this.typeTags).sort();
    this.tableFilterSelectObj.topic.options = this.parseFilterTags(this.topicTags).sort();
    this.tableFilterControls.topic.disable();

  }

  async ngAfterViewInit() {
    super.ngAfterViewInit();

    await this.checkAgencyRegionSetup();
    /** TOPIC doesn't display unless TYPE filter is selected */
    this.typeControlChanges = this.tableFilterControls.type.valueChanges.subscribe((selectedOptions: string) => {
      // Enable TOPIC
      if (selectedOptions?.length > 0) {
        this.tableFilterControls.topic.enable();
      }
      // Disable TOPIC, trigger filters
      else {
        this.tableFilterControls.topic.disable();
        this.resetTopicFilters();
      }
    });

    /**
     * Observe changes to the market categories checkboxes, and update the flag for whether they are all, appropriately, checked/unchecked
     */
    this.resourceCategories$.subscribe(async (data) => {
      this.resetPaginationState();
      this.retrieveAndSetupData();
      this.setupSearchSubscription();
      const anyVisibleMarketsUnChecked = this.parsedMarketTags.some(tag => {
        return data.some(stateTagObj => {
          if (stateTagObj.key === tag
            && stateTagObj.value.visible
            && !stateTagObj.value.checked) {
            return true;
          }
        });
      });
      if (anyVisibleMarketsUnChecked) {
        this.allValidCheckboxesChecked = false;
      } else {
        this.allValidCheckboxesChecked = true;
      }
    });

    /** Listen to paginator page changes */
    // if (this.paginator) {
      this.paginator.page.subscribe(() => {
        this.fetchCurrentPage();
      });
    // }


    this.sort.sortChange.subscribe(s => {
      this.sortMap = new Map().set(s.active, s.direction);
      // this.resetPaginationState();
      this.fetchCurrentPage();
    })

  }

  ngOnDestroy() {
    if (this.topicControlChanges) {
      this.topicControlChanges.unsubscribe();
    }
    if (this.typeControlChanges) {
      this.typeControlChanges.unsubscribe();
    }
  }

  parseFilterTag(tag: string): string {
    const parsed = this.parseTagName(tag);
    return this.castAsUpperCamel(parsed);
  }

  private castAsUpperCamel(tag: string): string {
    const upperName = tag.split('-').map(w => {
      if (w.length > 1) {
        const firstLetter = w[0];
        const otherLetters = w.substring(1);
        return firstLetter.toUpperCase() + otherLetters;
      } else {
        return w.toUpperCase();
      }
    }).join(' ');
    return upperName;
  }

  async retrieveAndSetupData() {
    await this.fetchCurrentPage();
  }

  async fetchCurrentPage() {
    const pageIndex = this.paginator?.pageIndex || 0;
    const pageSize = this.paginator?.pageSize || this.currentPageSize;
    const start = pageIndex * pageSize;

    const types = this.tableFilterControls.type.value || [];
    const topics = this.tableFilterControls.topic.value || [];
    const searchText = this.tableFilterControls.fileSearch.value || '';

    /** Build region filter for server-side filtering */
    const regions = this.buildRegionFilter();

    /** Fetch Data */
    const response = await this.resourcesListService.getDamData(
      this.servicePath,
      this.docRoot,
      types,
      topics,
      this.sortMap,
      this.tableFilterControls.dateRange.value,
      searchText,
      start,
      pageSize,
      regions
    );

    if (!response) {
      this.dataSet = [];
      this.resourcesDataSource = new MatTableDataSource<ResourcesListItem>([]);
      return;
    }

    this.dataSet = response.output || [];
    this.totalCount = response.totalCount || 0;

    /** Update facet counts from server response */
    if (response.facets) {
      this.updateFacetCounts(response.facets);
    }

    /** Setup DataSource/Table (client-side filtering only, no pagination) */
    this.resourcesDataSource = new MatTableDataSource<ResourcesListItem>(this.dataSet);


    /** Setup Column Filters (A) */
    this.resourcesDataSource.filterPredicate = this.createFilter();

    /** Setup Sort */
    this.resourcesDataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'lastModifiedDate':
          return this.parseDate(item.lastModifiedDate);
        default:
          return item[property];
      }
    }
    this.resourcesDataSource.sort = this.sort;

    /** Setup Paginator with server-side total */
    if (this.paginator) {
      this.paginator.length = this.totalCount;
    }

  }

  /** Parse comma-separated filter tags from @Input */
  parseFilterTags(tags: string): string[] {
    if (tags && tags.length > 0) {
      try {
        return tags.split(',').map(t => t.trim()).filter(t => t.length > 0);
      } catch (e) {
        console.error('Error parsing filter tags:', e);
        return [];
      }
    }
    return [];
  }

  /** Update facet counts from server response */
  updateFacetCounts(facets: any) {
    this.typeCounts.clear();
    this.topicCounts.clear();

    facets.types?.forEach((facet: any) => {
      this.typeCounts.set(facet.label.toUpperCase(), facet.count);
    });
    
    facets.topics?.forEach((facet: any) => {
      this.topicCounts.set(facet.label.toUpperCase(), facet.count);
    });
  }

  /** Reset pagination state when filters change */
  resetPaginationState() {
    this.typeCounts.clear();
    this.topicCounts.clear();
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
  }

  /** Filter Predicate Logic */
  createFilter() {
    const filterFunction = (data: any, filter: string): boolean => {
      const searchTerms = JSON.parse(filter);
      let isFilterSet = false;
      for (const col in searchTerms) {
        if (searchTerms[col].toString() !== '') {
          isFilterSet = true;
        } else {
          delete searchTerms[col];
        }
      }

      const nameSearch = () => {

        if (isFilterSet) {
          let searchTermMatches = {};

          for (const col in searchTerms) {

            if (!searchTermMatches[col]) {
              searchTermMatches[col] = false;
            }

            if (col === 'dateRange') {

              const datePublished = this.date.getDateAsMoment(data.lastModifiedDate);

              const dateCompare = this.date.getTodayAsMoment().subtract(searchTerms[col], 'days');
              searchTermMatches[col] = datePublished > dateCompare;

            } else if (col === 'fileSearch') {

              const query: string = searchTerms[col]?.join()?.toLowerCase();

              const fileName: string = data.fileName?.toLowerCase() || '';
              const fileNameMatches: boolean = fileName?.includes(query);

              const fileTitle: string = data.title?.toLowerCase() || '';
              const fileTitleMatches = fileTitle?.includes(query);

              searchTermMatches[col] = fileNameMatches || fileTitleMatches;

            } else if (searchTerms[col] && data[col]) {

              searchTermMatches[col] = searchTerms[col].some(
                (rowData: string) => data[col].some(colString => colString.trim().toLowerCase() === rowData.trim().toLowerCase())
              );

            }

          }

          if (!searchTermMatches) {
            return false;
          } else {
            for (const col in searchTerms) {
              if (!searchTermMatches[col]) {
                return false;
              }
            }
          }
          return true;
        } else {
          return true;
        }
      };

      return nameSearch();

    };

    return filterFunction;
  }

  /** Clearing Dropdown Filter Options */
  resetAllFilters() {
    Object.keys(this.tableFilterSelectObj).forEach((key: string) => {
      (this.tableFilterControls[key] as AbstractControl)?.reset();
    });

    this.applyFormFilters();
  }

  /** Clearing Dropdown Filter Options */
  resetTopicFilters() {
    this.tableFilterControls.topic.reset();

    this.applyFormFilters();
  }

  toggleAllCategories() {
    const tagsToToggle = this.parsedMarketTags.filter((tag: string) => {
      if (this.parsedAlwaysCheckedMarketTags.indexOf(tag) !== -1) {
        return tag;
      }
    });
    this.ngRedux.dispatch(toggleFlagResourceCategories(tagsToToggle, !this.allValidCheckboxesChecked));

    // Reset all other filters when toggling market categories
    this.resetAllFilters();
    this.resetPaginationState();
  }


  typeFilterChanged(event?: MatSelectChange) {

    /** Reset TOPIC selection */
    this.tableFilterControls.topic.reset();

    /** No TYPE selected; No TOPIC enabled */
    if (this.tableFilterControls.type.value.length === 0) {
      this.tableFilterControls.topic.disable();
    }

    // Reset pagination when filters change
    this.resetPaginationState();
    this.fetchCurrentPage();
  }

  // Reset pagination when filters change
  dropdownFilterChanged(event?: MatSelectChange) {
    this.resetPaginationState();
    this.fetchCurrentPage();
  }

  applyFormFilters() {
    const filter = {};

    Object.keys(this.tableFilterSelectObj).forEach((key: string) => {

      if (this.tableFilterControls[key].value) {
        
        filter[key] = [];

        /** OPTIONS for DROPDOWN control */
        if (Array.isArray(this.tableFilterControls[key].value)) {
          this.tableFilterControls[key].value.forEach((optionSelected: string) => {
            /** Only add the option as a filter if it was previously selected && able to be selected */
            if (this.tableFilterSelectObj[key]?.options.includes(optionSelected)) {
              filter[key].push(optionSelected);
            }
            //else {
            /** Don't add options that have no values (when doing a composite filter) */
            //}
          });
        }
        /** TEXT */
        else {
          filter[key].push(this.tableFilterControls[key].value);
        }
      }
    });

    /** Trigger filter update with filter string */
    this.resourcesDataSource.filter = JSON.stringify(filter);
  }

  setupSearchSubscription() {
    this.tableFilterControls.fileSearch.valueChanges.pipe(debounceTime(400)).subscribe(() => {
      // Trigger server-side search with pagination reset
      this.resetPaginationState();
      this.fetchCurrentPage();
    })
  }


  /** State events */
  toggleSingleCategory(categoryName: string) {
    this.ngRedux.dispatch(toggleFlagResourceCategory(categoryName));
  }

  emailDocument(document: ResourcesListItem) {
    // console.log(document);
    const resourcesListEmailModalId = 'resourcesListEmailModalId';
    const dialogRef = this.matDialog.open(ResourcesListEmailComponent, {
      id: resourcesListEmailModalId,
      maxHeight: '100vh',
      maxWidth: '650px',
      data: {
        contentPath: document.filePath
      }
    });
  }

  saveDocument(document: ResourcesListItem) {
    window.open(document.filePath, '_blank');
  }

  parseTagName(name: string) {
    const lastSlashIndex = name.lastIndexOf('/');
    const tagPath = name.substring(0, lastSlashIndex + 1);
    const stringToReplace = new RegExp(tagPath, 'g');
    const replaceWith = '';
    const parsedName = name.replace(stringToReplace, replaceWith);
    return parsedName;
  }

  async checkAgencyRegionSetup() {

    if (this.checkAgencyRegion === 'true') {
      this.selectedAgency = await this.entitlementsHelperService.getSelectedAgencyPromise();
    }

  }

  /**
   * Build region filter array for server-side filtering
   * Returns array containing 'ALL' and the specific agency region (if available)
   */
  buildRegionFilter(): string[] {
    
    if (this.isAuthoring || this.checkAgencyRegion !== 'true') {
      return [];
    }

    const regions: string[] = ['shopx:resources-list-tags/region/all'];
    
    if (this.selectedAgency?.region) {
      regions.push(`shopx:resources-list-tags/region/${this.selectedAgency.region.toLowerCase()}`);
    }

    return regions;
  }

  // TODO: somehow do this eleganytly if NDBCBS and 
  // if(actor.getBrand().toUpperCase() === "NDBCBS" && data.alphabetical.toUpperCase() === "APPLICATION FOR MEDICARE SUPPLEMENT"){
  //   on(row, ".resources-open-container:click", function (evt) {
  //     topic.publish('resourceOpenClicked', resourcePath);
  //     event.stop(evt);
  //     var currentUser = actor.getUser();
  //     var payload = {
  //         payload:{
  //             firstName:  currentUser.firstName,
  //             lastName:   currentUser.lastName,
  //             //change to agent acsid
  //             agentId:    currentUser.selectedAgency.agentACSId ? currentUser.selectedAgency.agentACSId : null,
  //             agencyId:   currentUser.selectedAgency.agencyId
  //         }
  //     };
  //     //post service to generate autofill PDF for ND Medicare Supplement
  //     hmxhr.post({
  //         url: "/cmcrst/x-services/public/medicare/suppliment/generatepdf",
  //         handleAs: "json",
  //         postData: JSON.stringify(payload),
  //         load: function(response){
  //             //download the file sent back from the service
  //             _downloadFile(response);
  //         }
  //     });
  // });
}
